import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const ESTADO_CONFIG = {
  pendiente:      { label: 'Pendiente',       bg: '#fff3cd', color: '#856404' },
  pagado_parcial: { label: 'Pagado Parcial',  bg: '#cfe2ff', color: '#0a3778' },
  pagado:         { label: 'Pagado',          bg: '#d1e7dd', color: '#0a3622' },
};

const Excedentes = ({ usuario, onLogout }) => {
  const [excedentes, setExcedentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);
  const [showPago, setShowPago] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [conductores, setConductores] = useState([]);
  const [viajes, setViajes] = useState([]);
  const [formPago, setFormPago] = useState({ monto: '', tipo_pago: 'efectivo', fecha: new Date().toISOString().slice(0,16), referencia: '' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      // allSettled: excedentes es lo esencial; conductores/viajes son opcionales
      // (finanzas no tiene permiso de conductores → no debe romper la pantalla).
      const [excRes, condRes, vRes] = await Promise.allSettled([
        api.get('/excedentes'),
        api.get('/conductores'),
        api.get('/viajes'),
      ]);
      if (excRes.status === 'rejected') throw excRes.reason;
      setExcedentes(excRes.value.data);
      setConductores(condRes.status === 'fulfilled' ? condRes.value.data : []);
      setViajes(vRes.status === 'fulfilled' ? vRes.value.data : []);
      setError('');
    } catch { setError('Error cargando excedentes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const registrarPago = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await api.post(`/excedentes/${seleccionado.id}/pagos`, {
        ...formPago,
        monto: parseFloat(formPago.monto),
        fecha: new Date(formPago.fecha).toISOString(),
      });
      setShowPago(false);
      setFormPago({ monto: '', tipo_pago: 'efectivo', fecha: new Date().toISOString().slice(0,16), referencia: '' });
      cargar();
    } catch (err) { setError(err.response?.data?.detail || 'Error registrando pago'); }
    finally { setSaving(false); }
  };

  const getViaje = (viaje_id) => viajes.find(v => v.id === viaje_id);
  const getConductor = (viaje_id) => {
    const viaje = getViaje(viaje_id);
    if (!viaje) return '-';
    return conductores.find(c => c.id === viaje.conductor_id)?.nombre || '-';
  };

  const pendientes = excedentes.filter(e => e.estado !== 'pagado');
  const pagados = excedentes.filter(e => e.estado === 'pagado');
  const totalPendiente = pendientes.reduce((s, e) => s + parseFloat(e.total_excedente || 0), 0);

  if (loading) return <Layout usuario={usuario} onLogout={onLogout} title="Excedentes"><div style={S.loading}>Cargando...</div></Layout>;

  return (
    <Layout usuario={usuario} onLogout={onLogout} title="Excedentes">
      {/* Resumen */}
      <div style={S.resumenRow}>
        <div style={S.resumenCard}>
          <div style={S.resumenNum}>{pendientes.length}</div>
          <div style={S.resumenLabel}>Pendientes de pago</div>
        </div>
        <div style={{...S.resumenCard, borderColor:'#CC0000'}}>
          <div style={{...S.resumenNum, color:'#CC0000'}}>RD$ {totalPendiente.toLocaleString('es-DO', {minimumFractionDigits:2})}</div>
          <div style={S.resumenLabel}>Total por cobrar</div>
        </div>
        <div style={S.resumenCard}>
          <div style={S.resumenNum}>{pagados.length}</div>
          <div style={S.resumenLabel}>Pagados</div>
        </div>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {excedentes.length === 0 ? (
        <div style={S.empty}><div style={{fontSize:'48px'}}>💰</div><p>No hay excedentes registrados aún.</p><p style={{fontSize:'12px', marginTop:'8px', color:'#aaa'}}>Los excedentes se calculan automáticamente al cerrar un viaje.</p></div>
      ) : (
        <div style={S.list}>
          {excedentes.map(exc => {
            const est = ESTADO_CONFIG[exc.estado] || {};
            const conductor = getConductor(exc.viaje_id);
            const viaje = getViaje(exc.viaje_id);
            return (
              <div key={exc.id} style={S.card}>
                <div style={S.cardHeader}>
                  <div style={S.cardLeft}>
                    <span style={{...S.badge, background: est.bg, color: est.color}}>{est.label}</span>
                    <span style={S.fecha}>{new Date(exc.creado_en).toLocaleDateString('es-DO')}</span>
                  </div>
                  {exc.estado !== 'pagado' && (
                    <button style={S.btnPagar} onClick={() => { setSeleccionado(exc); setShowPago(true); }}>
                      💵 Registrar Pago
                    </button>
                  )}
                </div>

                <div style={S.infoGrid}>
                  <div style={S.infoItem}>
                    <span style={S.infoLabel}>👤 Conductor</span>
                    <span style={S.infoVal}>{conductor}</span>
                  </div>
                  <div style={S.infoItem}>
                    <span style={S.infoLabel}>⛽ Gal. Excedente</span>
                    <span style={S.infoVal}>{parseFloat(exc.galones_excedente).toFixed(2)} gal</span>
                  </div>
                  <div style={S.infoItem}>
                    <span style={S.infoLabel}>💰 Total Excedente</span>
                    <span style={{...S.infoVal, color:'#CC0000', fontWeight:'800'}}>
                      RD$ {parseFloat(exc.total_excedente).toLocaleString('es-DO', {minimumFractionDigits:2})}
                    </span>
                  </div>
                  <div style={S.infoItem}>
                    <span style={S.infoLabel}>📅 Precio/Galón</span>
                    <span style={S.infoVal}>RD$ {parseFloat(exc.precio_galon_excedente).toFixed(2)}</span>
                  </div>
                </div>

                {/* Distribución */}
                <div style={S.distribucion}>
                  <div style={S.distItem}>
                    <span style={S.distLabel}>👤 Conductor ({exc.porcentaje_conductor}%)</span>
                    <span style={S.distVal}>RD$ {parseFloat(exc.monto_conductor).toLocaleString('es-DO', {minimumFractionDigits:2})}</span>
                  </div>
                  <div style={S.distItem}>
                    <span style={S.distLabel}>🏢 Empresa ({exc.porcentaje_empresa}%)</span>
                    <span style={S.distVal}>RD$ {parseFloat(exc.monto_empresa).toLocaleString('es-DO', {minimumFractionDigits:2})}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal pago */}
      {showPago && seleccionado && (
        <div style={S.modalOverlay} onClick={() => setShowPago(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h3 style={S.modalTitle}>💵 Registrar Pago de Excedente</h3>
            <div style={{fontSize:'12px', color:'#888', marginBottom:'16px'}}>
              Total excedente: <b style={{color:'#CC0000'}}>RD$ {parseFloat(seleccionado.total_excedente).toLocaleString('es-DO', {minimumFractionDigits:2})}</b>
            </div>
            {error && <div style={S.error}>{error}</div>}
            <form onSubmit={registrarPago} style={{display:'flex', flexDirection:'column', gap:'14px'}}>
              <div style={S.group}>
                <label style={S.label}>Monto a pagar RD$ *</label>
                <input style={S.input} type="number" step="0.01" min="0" value={formPago.monto} onChange={e => setFormPago({...formPago, monto: e.target.value})} placeholder="Ej: 5000" required />
              </div>
              <div style={S.group}>
                <label style={S.label}>Tipo de pago *</label>
                <select style={S.input} value={formPago.tipo_pago} onChange={e => setFormPago({...formPago, tipo_pago: e.target.value})}>
                  <option value="efectivo">💵 Efectivo</option>
                  <option value="transferencia">🏦 Transferencia</option>
                </select>
              </div>
              <div style={S.group}>
                <label style={S.label}>Fecha *</label>
                <input style={S.input} type="datetime-local" value={formPago.fecha} onChange={e => setFormPago({...formPago, fecha: e.target.value})} required />
              </div>
              {formPago.tipo_pago === 'transferencia' && (
                <div style={S.group}>
                  <label style={S.label}>Referencia</label>
                  <input style={S.input} value={formPago.referencia} onChange={e => setFormPago({...formPago, referencia: e.target.value})} placeholder="Número de transferencia" />
                </div>
              )}
              <div style={{display:'flex', gap:'10px'}}>
                <button type="button" style={S.btnCancel} onClick={() => setShowPago(false)}>Cancelar</button>
                <button type="submit" style={S.btnSave} disabled={saving}>{saving ? 'Guardando...' : '✅ Confirmar Pago'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

const S = {
  loading: {textAlign:'center', padding:'60px', color:'#666'},
  resumenRow: {display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'20px'},
  resumenCard: {background:'#fff', borderRadius:'12px', border:'1.5px solid #e0e0e0', padding:'16px', textAlign:'center', boxShadow:'0 2px 6px rgba(0,0,0,0.05)'},
  resumenNum: {fontSize:'22px', fontWeight:'800', color:'#1a1a1a', lineHeight:1},
  resumenLabel: {fontSize:'11px', color:'#888', marginTop:'5px', fontWeight:'500'},
  error: {background:'#fff0f0', border:'1px solid #ffcccc', color:'#CC0000', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px'},
  empty: {textAlign:'center', padding:'60px', color:'#999', background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0'},
  list: {display:'flex', flexDirection:'column', gap:'12px'},
  card: {background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0', padding:'16px', boxShadow:'0 2px 6px rgba(0,0,0,0.05)'},
  cardHeader: {display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'},
  cardLeft: {display:'flex', alignItems:'center', gap:'10px'},
  badge: {display:'inline-block', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'700'},
  fecha: {fontSize:'12px', color:'#888'},
  btnPagar: {background:'#CC0000', color:'#fff', border:'none', borderRadius:'7px', padding:'7px 14px', fontSize:'12px', fontWeight:'700', cursor:'pointer'},
  infoGrid: {display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px'},
  infoItem: {display:'flex', flexDirection:'column', gap:'2px'},
  infoLabel: {fontSize:'11px', color:'#888', fontWeight:'500'},
  infoVal: {fontSize:'13px', color:'#1a1a1a', fontWeight:'600'},
  distribucion: {background:'#f8f8f8', borderRadius:'8px', padding:'10px 12px', display:'flex', gap:'16px', flexWrap:'wrap'},
  distItem: {display:'flex', justifyContent:'space-between', flex:1, gap:'8px', minWidth:'200px'},
  distLabel: {fontSize:'12px', color:'#666'},
  distVal: {fontSize:'13px', fontWeight:'700', color:'#1a1a1a'},
  modalOverlay: {position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'},
  modal: {background:'#fff', borderRadius:'16px', padding:'24px', width:'100%', maxWidth:'400px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)'},
  modalTitle: {fontSize:'16px', fontWeight:'700', color:'#1a1a1a', marginBottom:'8px'},
  group: {display:'flex', flexDirection:'column', gap:'6px'},
  label: {fontSize:'12px', fontWeight:'600', color:'#555'},
  input: {padding:'10px 12px', borderRadius:'7px', border:'1.5px solid #e0e0e0', fontSize:'14px', outline:'none', color:'#1a1a1a'},
  btnCancel: {flex:1, padding:'10px', borderRadius:'7px', border:'1.5px solid #e0e0e0', background:'#fff', color:'#666', fontSize:'13px', fontWeight:'600', cursor:'pointer'},
  btnSave: {flex:1, padding:'10px', borderRadius:'7px', border:'none', background:'#CC0000', color:'#fff', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
};

export default Excedentes;
